import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { PileTypeId, ProjectId, StructuralPieceId, UnitTypeId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import type { ItemFactor } from './own-factor';
import type { Point } from './polygon';
import { STEEL_GAUGES, steelGaugeLabel, type SteelGauge } from './steel';

/**
 * A estaca e o tipo dela (Fase 27, etapa 1b — D1, D4, D5, F1, F3, F18).
 *
 * A estaca é a primeira **peça** da família nova (ADR-175): ela se mostra e se edita como o
 * ambiente — nome, lista, clique para editar —, mas não é `Room`. O ambiente soma em piso,
 * teto e paredes, e a estaca dentro de um bloco seria um ambiente sobreposto a outro. Por isso
 * a peça tem tabela, elemento no "Aplica a" e conta próprios, e nunca entra nas medidas de
 * AMBIENTE, PAREDE ou PILAR (RN-360).
 *
 * > **As regras em uma linha:** o tipo dá o diâmetro, o comprimento e o aço (RN-362); a
 * > estaca só conta depois de posicionada (RN-364); o nome não se repete no desenho (RN-376).
 */

// ── O tipo de estaca ────────────────────────────────────────────────────────────

/**
 * De onde veio o número da composição do tipo (Fase 39, D8, D9).
 *
 * `MEDIA` é a foto da taxa média das obras orçadas, tirada quando o tipo foi cadastrado ou mudou
 * de diâmetro, e o "Atualizar pela média" a troca. `DIGITADO` é o que alguém escreveu por cima:
 * fica até alguém voltar à média, e entra na média das outras obras como número próprio (D5).
 */
export type PileRateOrigin = 'MEDIA' | 'DIGITADO';

/** A foto da média (Fase 39, D8): o valor, o dia e de onde veio. */
export interface PileRateSnapshot {
  readonly rate: number;
  /** `aaaa-mm-dd`. */
  readonly at: string;
  readonly projects: number;
  readonly piles: number;
}

/** Quantos kg da bitola vão em **uma** estaca do tipo (D1). */
export interface PileSteel {
  readonly gauge: SteelGauge;
  readonly kgPerPile: number;
  /** Ausente é o aço cadastrado antes da Fase 39: digitado. */
  readonly origin?: PileRateOrigin;
  /** A média do diâmetro quando o número foi gravado — ao lado do digitado. */
  readonly average?: PileRateSnapshot;
}

/**
 * O fator de um item num serviço que mede a estaca, para este tipo (Fase 39, RN-504).
 *
 * É o índice da Fase 24 (RN-280) por **diâmetro**: quanto do item por unidade da medida do
 * serviço na estaca do tipo — "1,05 M³ por m³ de estaca", "0,0197 KG por kg de aço". O item é o
 * do catálogo da obra, pela chave do serviço e a dele; a perda já vem dentro do fator (D13).
 */
export interface PileItemRate {
  readonly serviceKey: string;
  readonly itemKey: string;
  readonly rate: number;
  readonly origin: PileRateOrigin;
  readonly average?: PileRateSnapshot;
}

/**
 * A chave de um item na composição: a mesma do lançamento do item (`itemQuantityKey`), para que
 * a conta ache o fator do tipo onde acha o número digitado.
 */
export function pileRateKey(serviceKey: string, itemKey: string): string {
  return `${serviceKey}»${itemKey}`;
}

/**
 * O tipo — "R40": Ø 40 cm × 16 m, com 31,56 kg de CA 50 - 16 mm e 4,54 kg de 6,3 mm.
 *
 * É da **obra**, como o índice: duas obras podem ter um R40 com comprimentos diferentes. A
 * proporção entre bitolas muda de um tipo para o outro (o 6,3 do R40 é 4,54 kg, o do R60 é
 * 6,66), e é por isso que o aço da estaca vem do tipo, e não da taxa da disciplina (D12).
 */
export interface PileType {
  readonly id: PileTypeId;
  readonly projectId: ProjectId;
  /** "R40", "R60" — o que o fim do nome da estaca escreve. */
  readonly name: string;
  /** Metros. 0,40 no R40. */
  readonly diameter: number;
  /** Metros. O comprimento padrão; a estaca pode ter o dela, com motivo. */
  readonly length: number;
  /** Na ordem das bitolas, uma linha por bitola, sem zero. */
  readonly steel: readonly PileSteel[];
  /**
   * A composição: o fator de cada item dos serviços que medem a estaca (Fase 39, RN-504). O item
   * sem fator aqui usa o índice da obra (RN-280); ausente é o tipo de antes da Fase 39.
   */
  readonly rates?: readonly PileItemRate[];
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export const PILE_ERROR = {
  TYPE_NAME_REQUIRED: 'TAKEOFF.PILE_TYPE_NAME_REQUIRED',
  TYPE_NAME_TAKEN: 'TAKEOFF.PILE_TYPE_NAME_TAKEN',
  TYPE_NAME_TOO_LONG: 'TAKEOFF.PILE_TYPE_NAME_TOO_LONG',
  NAME_TOO_LONG: 'TAKEOFF.PILE_NAME_TOO_LONG',
  TYPE_INVALID_DIMENSION: 'TAKEOFF.PILE_TYPE_INVALID_DIMENSION',
  TYPE_INVALID_STEEL: 'TAKEOFF.PILE_TYPE_INVALID_STEEL',
  // A composição do tipo (Fase 39, RN-504).
  TYPE_INVALID_RATE: 'TAKEOFF.RATE_AVERAGE_INVALID_RATE',
  TYPE_IN_USE: 'TAKEOFF.PILE_TYPE_IN_USE',
  NAME_REQUIRED: 'TAKEOFF.PILE_NAME_REQUIRED',
  NAME_TAKEN: 'TAKEOFF.PILE_NAME_TAKEN',
  TYPE_REQUIRED: 'TAKEOFF.PILE_TYPE_REQUIRED',
  INVALID_LENGTH: 'TAKEOFF.PILE_INVALID_LENGTH',
  LENGTH_REASON_REQUIRED: 'TAKEOFF.PILE_LENGTH_REASON_REQUIRED',
} as const;

/**
 * Os limites do contrato (`takeoff.ts`): o nome do tipo cabe em 80 caracteres e o da peça em
 * 160. O servidor recusa o que passa disso com 400 — e a gravação do rascunho é a lista
 * **inteira**, então um único nome comprido derrubava todas as gravações seguintes. O
 * domínio recusa antes, na tela, com o motivo.
 */
export const PILE_TYPE_NAME_MAX_LENGTH = 80;
export const PIECE_NAME_MAX_LENGTH = 160;

/** Os maiores que a obra tem: acima disso é vírgula no lugar errado. */
export const PILE_DIAMETER_MAX = 3;
export const PILE_LENGTH_MAX = 100;

/** O nome da peça cabe no que o servidor aceita? */
export function pieceNameTooLong(name: string): boolean {
  return name.trim().length > PIECE_NAME_MAX_LENGTH;
}

export function pieceNameTooLongMessage(rotulo: string): string {
  return `O nome do ${rotulo} passa de ${String(PIECE_NAME_MAX_LENGTH)} caracteres.`;
}

function positivo(valor: number): boolean {
  return Number.isFinite(valor) && valor > 0;
}

/** Três casas no kg e no metro: é o que a coluna guarda. */
function tres(valor: number): number {
  return Math.round(valor * 1000) / 1000;
}

export interface PileTypeFields {
  readonly name: string;
  readonly diameter: number;
  readonly length: number;
  readonly steel: readonly PileSteel[];
}

/**
 * Os campos do tipo, conferidos — o mesmo para criar e para corrigir.
 *
 * Nome único na obra, sem diferenciar caixa nem espaço (`normalizeName`): "r40" e "R40" seriam dois tipos com o
 * mesmo nome, e a estaca importada como "Estaca - E76-A - R40" não saberia qual escolher.
 */
export function checkPileTypeFields(
  fields: PileTypeFields,
  others: readonly Pick<PileType, 'id' | 'name' | 'deletedAt'>[],
  selfId?: PileTypeId,
): Result<PileTypeFields, DomainError> {
  const name = fields.name.trim();
  if (name === '') {
    return err(
      domainError(PILE_ERROR.TYPE_NAME_REQUIRED, 'O nome do tipo de estaca é obrigatório.'),
    );
  }
  if (name.length > PILE_TYPE_NAME_MAX_LENGTH) {
    return err(
      domainError(
        PILE_ERROR.TYPE_NAME_TOO_LONG,
        `O nome do tipo de estaca passa de ${String(PILE_TYPE_NAME_MAX_LENGTH)} caracteres.`,
        { length: name.length },
      ),
    );
  }
  const chave = normalizeName(name);
  if (
    others.some(
      (outro) =>
        outro.deletedAt === undefined && outro.id !== selfId && normalizeName(outro.name) === chave,
    )
  ) {
    return err(
      domainError(PILE_ERROR.TYPE_NAME_TAKEN, `Já existe o tipo de estaca ${name} nesta obra.`, {
        name,
      }),
    );
  }
  if (!positivo(fields.diameter) || !positivo(fields.length)) {
    return err(
      domainError(
        PILE_ERROR.TYPE_INVALID_DIMENSION,
        'O diâmetro e o comprimento do tipo de estaca devem ser maiores que zero.',
        { diameter: fields.diameter, length: fields.length },
      ),
    );
  }
  if (fields.diameter > PILE_DIAMETER_MAX || fields.length > PILE_LENGTH_MAX) {
    return err(
      domainError(
        PILE_ERROR.TYPE_INVALID_DIMENSION,
        fields.diameter > PILE_DIAMETER_MAX
          ? `O diâmetro do tipo de estaca passa de ${String(PILE_DIAMETER_MAX * 100)} cm: confira a medida.`
          : `A perfuração do tipo de estaca passa de ${String(PILE_LENGTH_MAX)} m: confira a medida.`,
        { diameter: fields.diameter, length: fields.length },
      ),
    );
  }
  const vistas = new Set<SteelGauge>();
  for (const linha of fields.steel) {
    if (!positivo(linha.kgPerPile) || vistas.has(linha.gauge)) {
      return err(
        domainError(
          PILE_ERROR.TYPE_INVALID_STEEL,
          `O aço de ${steelGaugeLabel(linha.gauge)} no tipo ${name} deve ser maior que zero e aparecer uma vez só.`,
          { gauge: linha.gauge, kgPerPile: linha.kgPerPile },
        ),
      );
    }
    vistas.add(linha.gauge);
  }
  return ok({
    name,
    diameter: tres(fields.diameter),
    length: tres(fields.length),
    // A origem e a foto da média vão junto (Fase 39): conferir o tipo não apaga de onde veio o aço.
    steel: STEEL_GAUGES.flatMap((info) => {
      const linha = fields.steel.find((candidata) => candidata.gauge === info.code);
      return linha === undefined ? [] : [{ ...linha, kgPerPile: tres(linha.kgPerPile) }];
    }),
  });
}

/**
 * A composição do tipo, conferida (Fase 39, RN-504, RN-506): fator maior que zero, um por item,
 * e a média junto quando o número segue a média. Fator zero é "sem fator" (RN-281): o item sai.
 */
export function checkPileRates(
  rates: readonly PileItemRate[],
): Result<readonly PileItemRate[], DomainError> {
  const vistos = new Set<string>();
  for (const linha of rates) {
    const chave = pileRateKey(linha.serviceKey, linha.itemKey);
    if (!positivo(linha.rate) || vistos.has(chave)) {
      return err(
        domainError(
          PILE_ERROR.TYPE_INVALID_RATE,
          'O fator de cada item do tipo de estaca deve ser maior que zero e aparecer uma vez só.',
          { serviceKey: linha.serviceKey, itemKey: linha.itemKey, rate: linha.rate },
        ),
      );
    }
    if (linha.origin === 'MEDIA' && linha.average === undefined) {
      return err(
        domainError(
          PILE_ERROR.TYPE_INVALID_RATE,
          'O fator que segue a média precisa da foto da média.',
          { serviceKey: linha.serviceKey, itemKey: linha.itemKey },
        ),
      );
    }
    vistos.add(chave);
  }
  return ok(rates);
}

/** Os kg de aço de uma estaca do tipo, todas as bitolas somadas. */
export function pileTypeSteelTotal(type: Pick<PileType, 'steel'>): number {
  return type.steel.reduce((soma, linha) => soma + linha.kgPerPile, 0);
}

// ── A peça ──────────────────────────────────────────────────────────────────────

/**
 * Que peça é. Por enquanto só a estaca; bloco, baldrame, radier, laje, pilar de contenção,
 * muro e escada entram nas etapas 2 a 5 (ADR-175).
 */
export type StructuralPieceKind = 'ESTACA';

/**
 * A peça desenhada — aqui, a estaca.
 *
 * Pertence à **área comum** do desenho do pavimento (F3, RN-361): a fundação não tem apto, e
 * a área comum já cabe em qualquer pavimento. Assim a quantidade continua somando por
 * tipologia, como todo o resto.
 */
export interface StructuralPiece {
  readonly id: StructuralPieceId;
  /** A tipologia de área comum do desenho do pavimento (Fundação ou Contenção). */
  readonly unitTypeId: UnitTypeId;
  readonly kind: StructuralPieceKind;
  /** "E76-A" — o nome do projeto de fundação. Único no desenho (RN-376). */
  readonly name: string;
  readonly pileTypeId: PileTypeId;
  /**
   * O centro da estaca, em metros, nas coordenadas da tipologia — as do contorno do ambiente.
   *
   * **Ausente é "a posicionar"** (D5): a estaca importada da planilha chega sem lugar e fica
   * na fila do "faltam N" até alguém clicar onde ela está. Enquanto isso **não conta** no
   * quantitativo (F18, RN-364).
   */
  readonly center?: Point;
  /** O comprimento desta estaca, quando não é o do tipo. Metros. Pede motivo (RN-362). */
  readonly lengthOverride?: number;
  readonly lengthReason?: string;
  /**
   * O fator próprio de cada item, por cima do fator do tipo (Fase 39, D14, RN-511) — "esta estaca
   * leva 1,10 de concreto por m³". Ausente ou vazio, todo item segue o tipo. Não pede motivo
   * (RN-513). Só vale para a estaca desenhada: a peça da planilha traz a quantidade pronta (RN-514).
   */
  readonly factors?: readonly ItemFactor[];
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

/**
 * A estaca marcada para um serviço — o teste de resistência, o controle tecnológico (D4).
 *
 * Uma marca por serviço: a estaca pode estar marcada para o teste, para o controle, para os
 * dois ou para nenhum, e cada serviço medido por `MARKED` conta só as suas (RN-365). No
 * rascunho o serviço vai pela chave do catálogo, como a quantidade decidida por tipologia; no
 * banco, pelo id.
 */
export interface PieceMark {
  readonly pieceId: StructuralPieceId;
  readonly serviceKey: string;
}

/** Posicionada = tem centro. Só ela entra no quantitativo (RN-364). */
export function isPositioned(piece: Pick<StructuralPiece, 'center' | 'deletedAt'>): boolean {
  return piece.deletedAt === undefined && piece.center !== undefined;
}

/** As que faltam posicionar, na ordem do nome — a fila do "faltam N" (D5). */
export function pilesToPlace<T extends Pick<StructuralPiece, 'name' | 'center' | 'deletedAt'>>(
  pieces: readonly T[],
): readonly T[] {
  return pieces
    .filter((piece) => piece.deletedAt === undefined && piece.center === undefined)
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true }));
}

/** O comprimento que vale: o da estaca, se corrigido; senão o do tipo. */
export function pileLength(
  piece: Pick<StructuralPiece, 'lengthOverride'>,
  type: Pick<PileType, 'length'>,
): number {
  return piece.lengthOverride ?? type.length;
}

/** O volume da estaca: π·d²/4 × comprimento, em m³, com três casas. */
export function pileVolume(
  piece: Pick<StructuralPiece, 'lengthOverride'>,
  type: Pick<PileType, 'diameter' | 'length'>,
): number {
  return roundQuantity(((Math.PI * type.diameter ** 2) / 4) * pileLength(piece, type), 3);
}

/**
 * O nome e o tipo da estaca, conferidos (RN-362, RN-376).
 *
 * O nome não se repete entre as estacas vivas da mesma tipologia, sem diferenciar caixa: a
 * fila de posicionar e a planilha importada se encontram pelo nome.
 */
export function checkPileFields(
  fields: { readonly name: string; readonly pileTypeId?: PileTypeId },
  siblings: readonly Pick<StructuralPiece, 'id' | 'name' | 'deletedAt'>[],
  selfId?: StructuralPieceId,
): Result<{ readonly name: string; readonly pileTypeId: PileTypeId }, DomainError> {
  const name = fields.name.trim();
  if (name === '') {
    return err(domainError(PILE_ERROR.NAME_REQUIRED, 'O nome da estaca é obrigatório.'));
  }
  if (pieceNameTooLong(name)) {
    return err(domainError(PILE_ERROR.NAME_TOO_LONG, pieceNameTooLongMessage('estaca')));
  }
  if (fields.pileTypeId === undefined) {
    return err(
      domainError(
        PILE_ERROR.TYPE_REQUIRED,
        `A estaca ${name} precisa de um tipo: é dele que sai o diâmetro.`,
      ),
    );
  }
  const chave = normalizeName(name);
  if (
    siblings.some(
      (outra) =>
        outra.deletedAt === undefined && outra.id !== selfId && normalizeName(outra.name) === chave,
    )
  ) {
    return err(
      domainError(PILE_ERROR.NAME_TAKEN, `Já existe a estaca ${name} neste desenho.`, { name }),
    );
  }
  return ok({ name, pileTypeId: fields.pileTypeId });
}

/**
 * O comprimento corrigido, conferido: positivo e com motivo (RN-362).
 *
 * Igual ao do tipo não é correção — volta a valer o do tipo, e o motivo some junto.
 */
export function checkLengthOverride(
  lengthOverride: number | undefined,
  reason: string | undefined,
  type: Pick<PileType, 'length'>,
): Result<{ readonly lengthOverride?: number; readonly lengthReason?: string }, DomainError> {
  if (lengthOverride === undefined || tres(lengthOverride) === type.length) return ok({});
  if (!positivo(lengthOverride)) {
    return err(
      domainError(PILE_ERROR.INVALID_LENGTH, 'O comprimento da estaca deve ser maior que zero.', {
        lengthOverride,
      }),
    );
  }
  if (lengthOverride > PILE_LENGTH_MAX) {
    return err(
      domainError(
        PILE_ERROR.INVALID_LENGTH,
        `O comprimento da estaca passa de ${String(PILE_LENGTH_MAX)} m: confira a medida.`,
        { lengthOverride },
      ),
    );
  }
  const motivo = reason?.trim() ?? '';
  if (motivo === '') {
    return err(
      domainError(PILE_ERROR.LENGTH_REASON_REQUIRED, 'Corrigir o comprimento do tipo pede motivo.'),
    );
  }
  return ok({ lengthOverride: tres(lengthOverride), lengthReason: motivo });
}
